import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, DollarSign, Clock, Sparkles, Trash2, BookOpen, Video, Package, Briefcase, Play, CheckCircle2, Lightbulb, Hammer, FileText, Calendar, Dumbbell, Link, NotebookPen, Mic, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface MonetizationProduct {
  id: string;
  product_type: string;
  title: string;
}

interface MonetizationProject {
  id: string;
  title: string;
  entry_type: string;
  niche: string | null;
  platform: string | null;
  last_step: string;
  created_at: string;
  updated_at: string;
  products?: MonetizationProduct[];
}

const STEP_LABELS: Record<string, { label: string; color: string }> = {
  entry: { label: "Getting Started", color: "text-muted-foreground" },
  input: { label: "Defining Input", color: "text-yellow-400" },
  ideas: { label: "Ideas Generated", color: "text-blue-400" },
  products: { label: "Ideas Generated", color: "text-blue-400" },
  building: { label: "Product Built", color: "text-purple-400" },
  pricing: { label: "Pricing Done", color: "text-green-400" },
  complete: { label: "Complete", color: "text-green-400" },
};

const ENTRY_TYPE_LABELS: Record<string, string> = {
  script: "From Script",
  idea: "Own Idea",
  discover: "AI Discovery",
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  ebook: "eBook",
  video_course: "Video Course",
  asset_pack: "Asset Pack",
  toolkit: "Toolkit",
  mini_course: "Mini Course",
  coaching: "Coaching",
  templates: "Templates",
  notion_dashboard: "Notion Dashboard",
  notion_template: "Notion Template",
  presets: "Presets/Packs",
  membership: "Membership",
  service: "Service",
  micro_app: "Micro App",
  swipe_file: "Swipe File",
  newsletter_pack: "Newsletter Pack",
  challenge_kit: "Challenge Kit",
  resource_bundle: "Resource Bundle",
  content_calendar: "Content Calendar",
  workbook: "Workbook",
  coaching_scripts: "Coaching Scripts",
};

const PRODUCT_ICONS: Record<string, React.ReactNode> = {
  ebook: <BookOpen className="w-3 h-3" />,
  video_course: <Video className="w-3 h-3" />,
  asset_pack: <Package className="w-3 h-3" />,
  toolkit: <Briefcase className="w-3 h-3" />,
  mini_course: <Sparkles className="w-3 h-3" />,
  coaching: <DollarSign className="w-3 h-3" />,
  swipe_file: <FileText className="w-3 h-3" />,
  notion_template: <FileText className="w-3 h-3" />,
  newsletter_pack: <Mail className="w-3 h-3" />,
  challenge_kit: <Dumbbell className="w-3 h-3" />,
  resource_bundle: <Link className="w-3 h-3" />,
  content_calendar: <Calendar className="w-3 h-3" />,
  workbook: <NotebookPen className="w-3 h-3" />,
  coaching_scripts: <Mic className="w-3 h-3" />,
};

const STATUS_DOT: Record<string, { color: string; icon: React.ReactNode }> = {
  entry: { color: "bg-muted-foreground", icon: null },
  input: { color: "bg-yellow-400", icon: null },
  ideas: { color: "bg-blue-400", icon: <Lightbulb className="w-3 h-3" /> },
  products: { color: "bg-blue-400", icon: <Lightbulb className="w-3 h-3" /> },
  building: { color: "bg-purple-400", icon: <Hammer className="w-3 h-3" /> },
  pricing: { color: "bg-green-400", icon: <CheckCircle2 className="w-3 h-3" /> },
  complete: { color: "bg-green-400", icon: <CheckCircle2 className="w-3 h-3" /> },
};

export default function MonetizationDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<MonetizationProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchProjects();
  }, [user]);

  const fetchProjects = async () => {
    try {
      const { data: projectsData, error: projectsError } = await supabase
        .from("monetization_projects")
        .select("*")
        .order("updated_at", { ascending: false });

      if (projectsError) throw projectsError;

      const projectIds = (projectsData || []).map(p => p.id);

      if (projectIds.length > 0) {
        const { data: productsData, error: productsError } = await supabase
          .from("monetization_products")
          .select("id, project_id, product_type, title")
          .in("project_id", projectIds);

        if (productsError) throw productsError;

        const projectsWithProducts = (projectsData || []).map(project => ({
          ...project,
          products: (productsData || []).filter(p => p.project_id === project.id),
        }));

        setProjects(projectsWithProducts);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleNewProject = () => {
    navigate("/app/monetization-ai/new");
  };

  const handleProjectClick = async (project: MonetizationProject) => {
    if (["building", "pricing", "complete"].includes(project.last_step)) {
      try {
        const { data: products, error } = await supabase
          .from("monetization_products")
          .select("id")
          .eq("project_id", project.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (error) throw error;

        if (products && products.length > 0) {
          const productId = products[0].id;
          if (project.last_step === "building") {
            navigate(`/app/monetization-ai/project/${project.id}/build/${productId}`);
          } else {
            navigate(`/app/monetization-ai/project/${project.id}/pricing/${productId}`);
          }
          return;
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
      navigate(`/app/monetization-ai/project/${project.id}/products`);
      return;
    }

    switch (project.last_step) {
      case "entry":
      case "input":
        navigate(`/app/monetization-ai/project/${project.id}/input`);
        break;
      case "ideas":
      case "products":
        navigate(`/app/monetization-ai/project/${project.id}/products`);
        break;
      default:
        navigate(`/app/monetization-ai/project/${project.id}/input`);
    }
  };

  const handleDeleteProject = async (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from("monetization_projects")
        .delete()
        .eq("id", projectId);

      if (error) throw error;
      setProjects(projects.filter((p) => p.id !== projectId));
      toast.success("Project deleted");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const ideasGenerated = projects.filter(p => ["products", "ideas", "building", "pricing", "complete"].includes(p.last_step)).length;
  const productBuilt = projects.filter(p => ["building", "pricing", "complete"].includes(p.last_step)).length;
  const fullyComplete = projects.filter(p => p.last_step === "complete").length;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl overflow-hidden flex-shrink-0">
              <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-2xl md:text-3xl font-bold">Monetization AI</h1>
              <p className="text-sm md:text-base text-muted-foreground truncate">Turn content into products</p>
            </div>
          </div>
          <Button variant="hero" size="default" onClick={handleNewProject} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
            New Product
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8">
          <div className="glass-card p-3 md:p-4 border-green-500/20">
            <div className="text-xl md:text-2xl font-bold text-green-400">{projects.length}</div>
            <div className="text-xs md:text-sm text-muted-foreground">Total Projects</div>
          </div>
          <div className="glass-card p-3 md:p-4 border-blue-500/20">
            <div className="text-xl md:text-2xl font-bold text-blue-400">{ideasGenerated}</div>
            <div className="text-xs md:text-sm text-muted-foreground">Ideas Generated</div>
          </div>
          <div className="glass-card p-3 md:p-4 border-purple-500/20">
            <div className="text-xl md:text-2xl font-bold text-purple-400">{productBuilt}</div>
            <div className="text-xs md:text-sm text-muted-foreground">Products Built</div>
          </div>
          <div className="glass-card p-3 md:p-4 border-green-500/20">
            <div className="text-xl md:text-2xl font-bold text-green-400">{fullyComplete}</div>
            <div className="text-xs md:text-sm text-muted-foreground">Fully Complete</div>
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card p-4 md:p-6 animate-pulse">
                <div className="h-5 md:h-6 bg-muted rounded w-3/4 mb-3 md:mb-4" />
                <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                <div className="h-4 bg-muted rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="glass-card p-8 md:p-12 text-center border-green-500/20">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto mb-4 md:mb-6">
              <DollarSign className="w-8 h-8 md:w-10 md:h-10 text-green-400" />
            </div>
            <h3 className="font-display text-lg md:text-xl font-semibold mb-2">No products yet</h3>
            <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md mx-auto">
              Start turning your content into profitable digital products with AI.
            </p>
            <Button variant="hero" onClick={handleNewProject}>
              <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Create Your First Product
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {projects.map((project) => {
              const stepInfo = STEP_LABELS[project.last_step] || { label: project.last_step, color: "text-muted-foreground" };
              const statusDot = STATUS_DOT[project.last_step] || STATUS_DOT.entry;
              const isStuck = ["entry", "input"].includes(project.last_step);

              return (
                <div
                  key={project.id}
                  onClick={() => handleProjectClick(project)}
                  className="glass-card p-4 md:p-6 border-green-500/20 hover:border-green-500/40 active:border-green-500/40 transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-3 md:mb-4">
                    <div className="flex-1 min-w-0 pr-2">
                      <h3 className="font-display text-base md:text-lg font-semibold text-foreground group-hover:text-green-400 transition-colors line-clamp-2">
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] md:text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                          {ENTRY_TYPE_LABELS[project.entry_type] || project.entry_type}
                        </span>
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive h-8 w-8 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Project</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{project.title}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                          <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => handleDeleteProject(e, project.id)}
                            className="bg-destructive hover:bg-destructive/90 w-full sm:w-auto"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {/* Product Type Badges */}
                  {project.products && project.products.length > 0 && (
                    <div className="flex flex-wrap gap-1 md:gap-1.5 mb-2 md:mb-3">
                      {project.products.slice(0, 2).map((product) => (
                        <span
                          key={product.id}
                          className="inline-flex items-center gap-1 text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full bg-primary/20 text-primary"
                        >
                          {PRODUCT_ICONS[product.product_type]}
                          <span className="hidden sm:inline">{PRODUCT_TYPE_LABELS[product.product_type] || product.product_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        </span>
                      ))}
                      {project.products.length > 2 && (
                        <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                          +{project.products.length - 2}
                        </span>
                      )}
                    </div>
                  )}

                  {project.niche && (
                    <p className="text-xs md:text-sm text-muted-foreground mb-2 md:mb-3 line-clamp-1">
                      Niche: {project.niche}
                    </p>
                  )}

                  {/* Status + Resume */}
                  <div className="flex items-center justify-between text-[10px] md:text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${statusDot.color}`} />
                      <span className={`${stepInfo.color} font-medium`}>{stepInfo.label}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground">{formatDate(project.updated_at)}</span>
                    </div>
                  </div>

                  {/* Resume button for stuck projects */}
                  {isStuck && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectClick(project);
                      }}
                    >
                      <Play className="w-3 h-3 mr-1.5" />
                      Resume
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
